import { hasAutomatedWorkflowsUseCase, useSignupUseCases, type SignupUseCase } from './signup-use-cases';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { AccountState, type Account } from '@/services/auth/types';

export type EssentialsOnboardingStatus = {
  isComplete: boolean;
  isLoading: boolean;
};

/**
 * STUB — always reports onboarding as incomplete, without firing any requests.
 *
 * TODO(SELF-140): derive this from live state (never stored progress). Flow
 * complete = a verified sending domain AND any workspace out of test mode,
 * checked client-side with existing data sources:
 * - `useAccountEmailSetup()` (`@/features/workspaces/delivery-mode-gating`)
 *   → `hasVerifiedSendingDomain`
 * - `useListWorkspaces()` (`@/features/workspaces`) → any workspace with
 *   `delivery_settings !== DeliverySettings.SendToOne` — exclude the injected
 *   sample workspace (hardcoded to 'normal', a false positive)
 * Gate the queries with `enabled` on eligibility so ineligible accounts (every
 * existing home test) keep firing zero new requests.
 */
export function useEssentialsOnboardingStatus(): EssentialsOnboardingStatus {
  return { isComplete: false, isLoading: false };
}

export type EssentialsOnboardingGate = {
  /**
   * Flag on + automated-workflows signup use case (mocked, SELF-89) + trial +
   * non-builder. Any signup role qualifies — the cohort is defined by the
   * use-case answer, not the role.
   */
  isEligible: boolean;
  /** Onboarding already finished in any workspace (stubbed false, SELF-140). */
  isComplete: boolean;
  isLoading: boolean;
};

export type EssentialsOnboardingEligibilityInput = {
  isFlagEnabled: boolean;
  useCases: readonly SignupUseCase[];
  account: Pick<Account, 'account_state' | 'plan_slug'>;
};

/**
 * Pure cohort rule: flag on + automated-workflows signup use case + trial +
 * non-builder. Any signup role qualifies — the cohort is defined by the
 * use-case answer, not the role.
 */
export function isEligibleForEssentialsOnboarding(input: EssentialsOnboardingEligibilityInput): boolean {
  const { isFlagEnabled, useCases, account } = input;
  return (
    isFlagEnabled &&
    hasAutomatedWorkflowsUseCase(useCases) &&
    account.account_state === AccountState.Free &&
    !isBuilderPlan(account)
  );
}

/**
 * Single cohort decision for the essentials onboarding takeover: who sees the
 * `/onboarding/*` pages and the home checklist instead of today's screens.
 * Everyone else keeps current behavior (non-builder trials still get the
 * workspace-setup screen). Callers must render below resolved auth — the
 * onboarding layout holds on `useAuthQuery().isLoading`, and the home page
 * renders under `_authenticated`.
 *
 * While `isLoading` is true, `isEligible`/`isComplete` are provisional —
 * callers must not act on them (e.g. redirect) until it settles. Today every
 * source is a synchronous stub, but SELF-89 (use cases) and SELF-140
 * (completion) make these real async reads.
 */
export function useEssentialsOnboardingGate(): EssentialsOnboardingGate {
  const isFlagEnabled = useFeatureFlag(AccountFeatureFlag.EssentialsOnboarding);
  const { account } = useAuth();
  const { useCases, isLoading: areUseCasesLoading } = useSignupUseCases();
  const { isComplete, isLoading: isStatusLoading } = useEssentialsOnboardingStatus();

  return {
    isEligible: isEligibleForEssentialsOnboarding({ isFlagEnabled: isFlagEnabled === true, useCases, account }),
    isComplete,
    isLoading: isStatusLoading || areUseCasesLoading,
  };
}
